import React, { useState, useEffect } from 'react'
import {Star, Quote, ChevronRight, X} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { ReviewableElement } from './ReviewableElement'

interface Testimonial {
  _id: string
  firstName: string
  position: string
  company: string
  testimonial: string
  rating?: number
  isVisible: boolean
  logo?: string
  website?: string
}

export function TestimonialsSection() {
  const [allTestimonials, setAllTestimonials] = useState<Testimonial[]>([])
  const [displayedTestimonials, setDisplayedTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null)

  const MAX_LENGTH = 200
  const DISPLAY_COUNT = 6

  // Fonction pour mélanger un tableau aléatoirement
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  // Charger tous les témoignages
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const { list } = await lumi.entities.testimonials.list({
          filter: { isVisible: true },
          sort: { createdAt: -1 }
        })
        const shuffled = shuffleArray(list || [])
        setAllTestimonials(shuffled)
        setDisplayedTestimonials(shuffled.slice(0, DISPLAY_COUNT))
      } catch (error) {
        console.error('Erreur lors du chargement des témoignages:', error)
        setAllTestimonials([])
        setDisplayedTestimonials([])
      } finally {
        setLoading(false)
      }
    }
    fetchTestimonials()
  }, [])

  // Rotation automatique toutes les 30 secondes
  useEffect(() => {
    if (allTestimonials.length <= DISPLAY_COUNT) return

    const interval = setInterval(() => {
      setAllTestimonials(prev => {
        const shuffled = shuffleArray(prev)
        setDisplayedTestimonials(shuffled.slice(0, DISPLAY_COUNT))
        return shuffled
      })
    }, 30000) // 30 secondes

    return () => clearInterval(interval)
  }, [allTestimonials.length])

  if (loading) {
    return (
      <section id="temoignages" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          <p className="text-gray-600 mt-4">Chargement des témoignages...</p>
        </div>
      </section>
    )
  }

  if (displayedTestimonials.length === 0) {
    return null
  }

  return (
    <section id="temoignages" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <ReviewableElement section="testimonials" contentKey="testimonials_title" elementType="text">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ils nous font confiance
            </h2>
          </ReviewableElement>
          <ReviewableElement section="testimonials" contentKey="testimonials_subtitle" elementType="text">
            <p className="text-xl text-gray-600">
              Découvrez les retours d'expérience de nos membres et partenaires
            </p>
          </ReviewableElement>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedTestimonials.map((testimonial) => {
            const isLong = testimonial.testimonial.length > MAX_LENGTH
            const displayText = isLong 
              ? testimonial.testimonial.substring(0, MAX_LENGTH) + '...' 
              : testimonial.testimonial

            return (
              <div
                key={testimonial._id}
                className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl shadow-lg hover:shadow-xl transition-all p-8 relative"
              >
                <div className="absolute top-6 right-6 opacity-20">
                  <Quote className="w-12 h-12 text-teal-600" />
                </div>
                
                {testimonial.rating && (
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                )}

                <p className="text-gray-700 leading-relaxed italic mb-6 relative z-10 whitespace-pre-line">
                  "{displayText}"
                </p>

                {isLong && (
                  <button
                    onClick={() => setSelectedTestimonial(testimonial)}
                    className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold mb-4 transition-colors group"
                  >
                    <span>Voir plus</span>
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}

                <div className="pt-4 border-t-2 border-teal-600">
                  <div className="flex items-center gap-4 mb-3">
                    {testimonial.logo && (
                      <img 
                        src={testimonial.logo} 
                        alt={testimonial.company} 
                        className="w-12 h-12 object-contain rounded"
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-lg mb-1">{testimonial.firstName}</p>
                      <p className="text-gray-500 text-sm">{testimonial.position}</p>
                    </div>
                  </div>
                  <p className="text-gray-900 font-semibold text-xl mb-1">{testimonial.company}</p>
                  {testimonial.website && (
                    <a 
                      href={testimonial.website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-teal-600 text-sm hover:underline inline-flex items-center gap-1"
                    >
                      {testimonial.website}
                      <ChevronRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Popup Modal for Full Testimonial */}
        {selectedTestimonial && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedTestimonial(null)}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-cyan-600 p-6 rounded-t-2xl">
                <div className="flex justify-between items-start">
                  <div className="flex-1 flex items-center gap-4">
                    {selectedTestimonial.logo && (
                      <img 
                        src={selectedTestimonial.logo} 
                        alt={selectedTestimonial.company} 
                        className="w-16 h-16 object-contain bg-white rounded-lg p-2"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-white">{selectedTestimonial.firstName}</h3>
                        {selectedTestimonial.rating && (
                          <div className="flex gap-1">
                            {[...Array(selectedTestimonial.rating)].map((_, i) => (
                              <Star key={i} className="w-5 h-5 fill-yellow-300 text-yellow-300" />
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-cyan-100 text-sm">{selectedTestimonial.position}</p>
                      <p className="text-white font-semibold text-xl mb-1">{selectedTestimonial.company}</p>
                      {selectedTestimonial.website && (
                        <a 
                          href={selectedTestimonial.website} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-cyan-100 text-sm hover:underline inline-flex items-center gap-1"
                        >
                          {selectedTestimonial.website}
                          <ChevronRight className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTestimonial(null)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
              
              <div className="p-8">
                <div className="relative">
                  <Quote className="absolute -top-4 -left-2 w-16 h-16 text-teal-200 opacity-50" />
                  <p className="text-gray-700 text-lg leading-relaxed italic relative z-10 whitespace-pre-line">
                    "{selectedTestimonial.testimonial}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
